import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { scheduleDate, getDateTimeZoneHandled } from '../../../utility/utils/dateUtil';

import cancelSalesAgreementClientNew from '../../../utility/apiClients/account/cancelSalesAgreementClient';
import Storage from '../../../utility/features/helpers/storageHelper';

import {Modal} from "bootstrap";
import { customerServiceNumberFormatted } from '../../../utility/utils/formatter';

const ServiceBottomTab = ({
  data,
  labels,
  year,
  salesAgrNum,
  typeList,
  resClientCallStatus,
  hideButton,
  serviceListCall,
  serviceDetailsCss
}) => {
  // Preserve original state name to avoid downstream changes
  const [is_SSCLinkVisible, setIs_SSCLinkVisible] = useState(false);

  // Values previously set on `this`
  const serviceAddress = useMemo(() => Storage.serviceAddressObj(), []);
  const customerNumber = useMemo(() => Storage.customerNumber(), []);
  const CancelReason = "AFFORD";
  const Employee = "119430";

  // Ref to the request cancel modal rendered here
  const requestCancelModalRef = useRef(null);

  /** Bootstrap 5 native Modal instance getter (if available) */
  const getBootstrapModalInstance = useCallback((el, options = { backdrop: false }) => {
    if (!el || typeof window === "undefined") return null;
    const { bootstrap } = window;
    if (!bootstrap?.Modal) return null;
    try {
      return bootstrap.Modal.getInstance(el) || new bootstrap.Modal(el, options);
    } catch {
      return new bootstrap.Modal(el, options);
    }
  }, []);

  /** Show a modal element (Bootstrap 5 if available, else fallback) */
  const showModal = useCallback(
    (el) => {
      if (!el) return;
      const instance = getBootstrapModalInstance(el, { backdrop: false });
      if (instance) {
        instance.show();
        return;
      }
      // Fallback: toggle classes/ARIA (no backdrop since original used data-backdrop="false")
      el.classList.add("show");
      el.style.display = "block";
      el.removeAttribute("aria-hidden");
      el.setAttribute("aria-modal", "true");
      document.body.classList.add("modal-open");
    },
    [getBootstrapModalInstance]
  );

  /** Hide a modal element (Bootstrap 5 if available, else fallback) */
  const hideModal = useCallback(
    (el) => {
      if (!el) return;
      const instance = getBootstrapModalInstance(el);
      if (instance) {
        instance.hide();
        return;
      }
      el.classList.remove("show");
      el.style.display = "";
      el.setAttribute("aria-hidden", "true");
      el.removeAttribute("aria-modal");
      document.body.classList.remove("modal-open");
    },
    [getBootstrapModalInstance]
  );

  /** Submit cancel request handler (no jQuery) */
  const submitCancelRequest = useCallback(
    (e, salesAgrNumParam, reason, employee) => {
      e.preventDefault();
      const btn = e.currentTarget;
      btn.disabled = true;

      cancelSalesAgreementClientNew(salesAgrNumParam, reason, employee).then(
        (res) => {
          btn.disabled = false;

          if (
            res?.data &&
            Array.isArray(res.data.messages) &&
            res.data.messages.length > 0 &&
            res.data.messages[0].CODE === "OK"
          ) {
            // Hide the request modal
            const requestEl =
              requestCancelModalRef.current ||
              document.getElementById(`RequestCancelServiceModal${salesAgrNumParam}`);
            hideModal(requestEl);

            // Update success modal subtext and show it
            const successId = `RequestCancelServiceSuccessModal${salesAgrNumParam}`;
            const successEl = document.getElementById(successId);
            if (successEl) {
              const subText = successEl.querySelector(".RequestCancelServiceSuccessModalSubText");
              if (subText) {
                subText.textContent = res.data.messages[0].DESCRIPTION || "";
              }
              showModal(successEl);
            }
          }
        },
        (error) => {
          btn.disabled = false;
          // TODO: add error toast/alert if needed
        }
      );
    },
    [hideModal, showModal]
  );

  /** Close request modal (Return to MyAccount button / close icon) */
  const handleCloseRequestModal = useCallback(
    (e) => {
      e.preventDefault();
      const el =
        requestCancelModalRef.current ||
        document.getElementById(`RequestCancelServiceModal${data.salesAgrNum}`);
      hideModal(el);
    },
    [data.salesAgrNum, hideModal]
  );

  // Cleanup when component unmounts
  useEffect(() => {
    return () => {
      if (requestCancelModalRef.current) hideModal(requestCancelModalRef.current);
    };
  }, [hideModal]);

  // === Original render logic preserved ===
  const salesAgrmtNum = data.salesAgrNum;

  let reSchedulableType = labels.rescheduleServiceSuccessNote;
  let reScheduledComponent = (
    <NotReschedulable
      labels={labels}
      is_SSCLinkVisible={is_SSCLinkVisible}
      salesAgrNum={salesAgrmtNum}
      data={data}
      year={year}
      typeList={typeList}
      resClientCallStatus={resClientCallStatus}
      hideButton={hideButton}
      serviceListCall={serviceListCall}
      serviceDetailsCss={serviceDetailsCss}
    />
  );

  if (data.targetScheduleDate && data.svcLineDesc) {
    reSchedulableType = labels.rescheduleServiceFailNote;
    const seven = new Date();
    seven.setDate(seven.getDate() + 7);
    const woDate = getDateTimeZoneHandled(data.woScheduledDate);
    if (seven.getTime() > woDate.getTime()) reSchedulableType = labels.rescheduleServiceOnWay;

    reScheduledComponent = (
      <Reschedulable
        data={data}
        labels={labels}
        year={year}
        reSchedulableType={reSchedulableType}
        is_SSCLinkVisible={is_SSCLinkVisible}
        salesAgrNum={salesAgrmtNum}
        serviceDetailsCss={serviceDetailsCss}
      />
    );
  }

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className={`${serviceDetailsCss['bg-white']} p-10 ${serviceDetailsCss['tru-mma-main-service-box']}`}>
            <div className="container-fluid p-0">
              <div className="align-items-center row">{reScheduledComponent}</div>

              {/* Request Cancel Service Modal */}
              {is_SSCLinkVisible && (
                <div
                  className="modal"
                  id={`RequestCancelServiceModal${salesAgrmtNum}`}
                  tabIndex="-1"
                  role="dialog"
                  aria-labelledby={`RequestCancelServiceModal${salesAgrmtNum}`}
                  data-backdrop="false"
                  ref={requestCancelModalRef}
                >
                  <div className="modal-dialog modal-md mt-100" role="document">
                    <div className="modal-content">
                      <div className="modal-header py-1">
                        <div className="col-sm-8" />
                        <a
                          className="col-sm-4 text-right pr-0 mt-10"
                          href="#"
                          onClick={handleCloseRequestModal}
                        >
                          <label>
                            <i
                              className="fa fa-times tg-tablestr-cur"
                              aria-hidden="true"
                              style={{ color: "#046a38" }}
                            />
                          </label>
                        </a>
                      </div>

                      <div className="modal-body px-0 py-0">
                        <div className="p-20">
                          <div className="row">
                            <div className="col-sm-12 text-center mb-13 text-green joinMeModalBody">
                              Confirm your cancellation request
                            </div>

                            <div className="col-sm-12 text-green joinMeModalSubText">
                              If you have a service scheduled in the next 48 hours, please call
                              Customer Service at 1-877-760-0075 as it may take up to 48hrs to
                              process your request.
                              <br />
                              <br />
                              Please note this will cancel all future applications for this
                              service. If you have multiple TruGreen services, you will need to
                              submit a separate request for each service from MyAccount.
                              <br />
                              <br />
                              Would you like to proceed?
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-sm-12 text-center mt-30 mb-30 modalMobile">
                              <button
                                className="loyalty-Join-Button ml-20 col-sm-6 mr-30 tg-tablestr-cur"
                                onClick={(e) =>
                                  submitCancelRequest(e, salesAgrmtNum, CancelReason, Employee)
                                }
                              >
                                Submit Cancel Request
                              </button>

                              <button
                                className="loyality-Cancel-Button col-sm-6 ml-20 mr-30 tg-tablestr-cur"
                                onClick={handleCloseRequestModal}
                              >
                                Return to MyAccount
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* success modal */}
              {is_SSCLinkVisible && <div
                className="modal"
                id={"RequestCancelServiceSuccessModal"+salesAgrmtNum}
                tabIndex="-1"
                role="dialog"
                aria-labelledby={"RequestCancelServiceSuccessModal"+salesAgrmtNum}
                data-backdrop="false"
              >
                <div className="modal-dialog modal-md mt-100" role="document">
                  <div className="modal-content">
                    <div className="modal-header py-1">
                      <div className="col-sm-8"></div>
                      <a className="col-sm-4 text-right pr-0 mt-10">
                        <label>
                          <i
                            className="fa fa-times tg-tablestr-cur"
                            data-dismiss="modal"
                            aria-hidden="true"
                            style={{ color: '#046a38' }}
                          ></i>
                        </label>
                      </a>
                    </div>
                    <div className="modal-body px-0 py-0">
                      <div className="p-20">
                        <div className="row">
                          <div className="col-sm-12 text-center mb-13 text-green joinMeModalBody">
                            Cancellation sucessfull! 
                          </div>
                          <div className="col-sm-12 text-green RequestCancelServiceSuccessModalSubText">
                          
                          </div>
                          
                        </div>
                        <div className="row">
                          <div className="col-sm-12 text-center mt-30 mb-30 modalMobile">
                              <button className="loyality-Cancel-Button col-sm-6 ml-20 mr-30 tg-tablestr-cur" data-dismiss='modal' aria-label='Close'>Ok</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              }
            </div>
          </div>
        </div>
      </div>
    </>
  );
};


function NotReschedulable({ labels, is_SSCLinkVisible, salesAgrNum, serviceDetailsCss, ...props }) {

  const refCallIVRModalRef = useRef(null);

  const onScheduleNow = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event' : 'account_action',
      'category': 'Account',
      'action': 'schedule now',
      'label': 'scheduleNow',
      'customer_id': (Storage.customerNumber() || {}),
      'error' : {
        'reason' : ''
      }})
    

    /* open map modal */
    if(Modal && refCallIVRModalRef.current){
      const refCallIVRModalPopup = new Modal(refCallIVRModalRef.current, {
          backdrop: 'static',
          keyboard: false
      });

      refCallIVRModalPopup.show();
      
    }
  }
  
  return (
    <>
      <div className='col-12 '>
        <div className={`${serviceDetailsCss['gfs-20']} ${serviceDetailsCss['gfw-4']} glh-1_1 ${serviceDetailsCss['mb-10']}`}>
          {labels.nextTargetService}
        </div>
      </div>
      <div className='col-12 col-md'>
        <div className='align-items-center d-md-flex mma--not-scheduled text-center text-md-left'>
          <div className='mr-md-10'>
            <img src='/images/error-notification-icon.png' alt='Not Scheduled' className={`img-fluid ${serviceDetailsCss['mma--service-schedule_icon']} mma--not-scheduled_icon`} />
          </div>
          <div className={`${serviceDetailsCss['gfs-16']} glh-1_2 ${serviceDetailsCss['text-gray-med']}`}>
            {labels.notScheduled}
          </div>
        </div>
        {is_SSCLinkVisible && <a href="#" data-toggle='modal' data-target={'#RequestCancelServiceModal'+salesAgrNum} className={`${serviceDetailsCss['service-line']}`} style={{textDecoration:"underline"}}>Request to Cancel Service Agreement</a>}
      </div>
      <div className='col-12 col-md'>
        <div className={`mb-10 ${serviceDetailsCss['mma--postpone-button']} text-center text-md-left`}>
          <button onClick={onScheduleNow} className={`${serviceDetailsCss['btnRequestNewServiceDate']} btn btn-sm`} >
            {labels.scheduleNow}
          </button>
        </div>
      </div>
      <div className={`modal modal--tutorial-prompt `} data-bs-backdrop='static' ref={refCallIVRModalRef} role='dialog' tabIndex='-1'>
        <div className={`modal-dialog modal-dialog-centered acc-dialog-modal p-0`} role='document' >
          <div className={`measure-tool-model ${serviceDetailsCss['bg-white']} modal-content pb-20 position-relative px-16 text-center`}>
            
            <div className="modal-header border-0"><button className="btn-close leadform-close-btn" aria-label="Close" type="button" data-bs-dismiss="modal"></button></div>
            <div className='modal-body p-0'>

              
              <section className="custom-quote">
                <div className="container">
                    <div className="row">
                      <div className="col-12">
                        <div className="custom-quote-box">
                          <div className="row">
                            <div className="col-md-12">
                              <div style={{ paddingBottom: '30px' }}>
                                <div className={`${serviceDetailsCss['gfs-20']} ${serviceDetailsCss['gfw-4']}`}>{`Please call this number to schedule a service`}</div>
                                <a href={`tel:${customerServiceNumberFormatted}`} className={`${serviceDetailsCss['gfs-24']} ${serviceDetailsCss['gfw-7']}`} style={{textDecoration: "none"}} >
                                  {customerServiceNumberFormatted}
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Reschedulable({ data, labels, reSchedulableType, is_SSCLinkVisible, salesAgrNum, serviceDetailsCss }) {
  const reScdate = scheduleDate(data.targetScheduleDate);
  const srvName = data.svcLineDesc;

  return (
    <>
      <div className='col-12 col-md-6'>
        <div className={`${serviceDetailsCss['gfs-20']} ${serviceDetailsCss['gfw-4']} glh-1_1 mb-10`}>
          {labels.nextTargetService}
        </div>
        <div className={`glh-1_2 ${serviceDetailsCss['gfw-4']}`}>
          <div className='mb-5'>
            <span className='g-text-case--capitalize'>{srvName}:{' '}</span>
            {data.svcLine && <span className={`${serviceDetailsCss['gfw-7']} mb-10 mb-md-0 ${serviceDetailsCss['service-line']} service-line--${(data.svcLine).toLowerCase()}`}>
              {reScdate}
            </span>}
            <div className='gfs-12 mt-10' />
          </div>
        </div>
      </div>
      <div className='col-12 col-md-6'>
        <div className='align-items-center d-md-flex mma--service-soon text-center text-md-left'>
          <div className={`${serviceDetailsCss['gfw-4']} glh-1_2`} dangerouslySetInnerHTML={{
            __html: reSchedulableType
          }} />
        </div>
        {is_SSCLinkVisible && <a href="#" data-toggle='modal' data-target={'#RequestCancelServiceModal'+salesAgrNum} className={`${serviceDetailsCss['service-line']}`} style={{textDecoration:"underline"}}>Request to Cancel Service Agreement</a>}
      </div>
    </>
  );
}

function OopsServiceFail({ data, labels, status, serviceDetailsCss }) {
  const reScdate = scheduleDate(data.targetScheduleDate);
  const srvName = data.svcLineDesc;
  let success = status;

  return (
    <>
      <div className='col-12 col-md-6'>
        <div className={`${serviceDetailsCss['gfs-20']} ${serviceDetailsCss['gfw-4']} glh-1_1 mb-10`}>
          {labels.nextTargetService}
        </div>
        <div className={`glh-1_2 ${serviceDetailsCss['gfw-4']}`}>
          <div className='mb-5'>
            <span className='g-text-case--capitalize'>{srvName}:{' '}</span>
            {data.svcLine && <span className={`${serviceDetailsCss['gfw-7']} mb-10 mb-md-0 ${serviceDetailsCss['service-line']} service-line--${(data.svcLine).toLowerCase()}`}>
              {reScdate}
            </span>}
            <div className='gfs-12 mt-10' />
          </div>
        </div>
      </div>
      {!success && <div className='col-12 col-md-6'>
        <div className='align-items-center d-md-flex mma--scheduling-error text-center text-md-left'>
          <div className='mr-md-10'>
            <img src='/images/error-notification-icon.png' alt='Error' className='img-fluid mma--service-schedule_icon' />
          </div>
          <div className={`${serviceDetailsCss['gfs-16']} glh-1_2`}>
            <span className={`${serviceDetailsCss['gfw-7']} text-danger`} >{labels.textOops}</span><span dangerouslySetInnerHTML={{
              __html: labels.oopsService
            }} />
          </div>
        </div>
      </div>}
      {success && <div className='col-12 col-md-6'>
        <div className='align-items-center d-md-flex mma--scheduling-error text-center text-md-left'>
          <div className='mr-md-10'>
            <img src='/images/check-circle-solid.svg' alt='Success' className='img-fluid mma--service-schedule_icon' />
          </div>
          <div className={`${serviceDetailsCss['gfs-16']} glh-1_2`}> {labels.weAreProcessing}</div>
        </div>
      </div>}
    </>
  );
}

export default ServiceBottomTab;